import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Leaf, 
  Target, 
  Eye, 
  Award, 
  Users, 
  TrendingUp, 
  ShieldCheck,
  Wheat,
  Sprout,
  Sun,
  Droplets
} from 'lucide-react';
import { Button } from '../components/ui/button';

const stats = [
  { number: '5K+', label: 'Farmers Served', icon: Users },
  { number: '130+', label: 'Seed Varieties', icon: Sprout },
  { number: '18+', label: 'Years Experience', icon: Award },
  { number: '98%', label: 'Germination Rate', icon: TrendingUp },
];

const categories = [
  { name: 'Cereals', icon: Wheat, description: 'Wheat, Rice, Maize' },
  { name: 'Pulses', icon: Leaf, description: 'Lentils, Chickpeas' },
  { name: 'Vegetables', icon: Sprout, description: 'Tomato, Onion, Carrot' },
  { name: 'Oilseeds', icon: Sun, description: 'Sunflower, Mustard' },
];

const features = [
  { title: 'Premium Quality', description: 'Certified seeds with highest germination rates', icon: ShieldCheck },
  { title: 'Research Backed', description: 'Varieties developed through extensive R&D', icon: Target },
  { title: 'Expert Support', description: 'Agronomists available for cultivation guidance', icon: Users },
  { title: 'Wide Distribution', description: 'Network spanning across all major regions', icon: TrendingUp },
];

export default function HomePage() {
  return (
    <div className="page-transition">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1763257494793-b2bde45acd96?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="hero-gradient absolute inset-0" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Leaf className="w-4 h-4" />
                Premium Quality Seeds Since 2006
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-6"
            >
              Growing Your Future with
              <span className="text-secondary"> Premium Seeds</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-white/90 text-lg sm:text-xl mb-8 leading-relaxed"
            >
              Dream Field Agri solution delivers certified, high-germination seeds 
              to farmers across the nation. Our commitment to quality ensures 
              bountiful harvests season after season.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-wrap gap-4"
            >
              <Link to="/products">
                <Button className="btn-primary text-base px-8 py-4" data-testid="hero-explore-btn">
                  Explore Products
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/dealer-inquiry">
                <Button className="btn-outline bg-white/10 text-white border-white hover:bg-white hover:text-primary text-base px-8 py-4">
                  Become a Dealer
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        >
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-1">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-3 bg-white/80 rounded-full"
            />
          </div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="stats-card text-center p-6 rounded-2xl bg-stone-50"
                >
                  <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="font-heading font-bold text-3xl text-stone-900 mb-1">{stat.number}</h3>
                  <p className="text-stone-600 text-sm">{stat.label}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block text-primary font-semibold text-sm mb-4 uppercase tracking-wide">About Us</span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-stone-900 mb-6">
                Growing Green Together Since 1985
              </h2>
              <p className="text-stone-600 text-lg leading-relaxed mb-6">
                At Dream Field Agri solution, we see agriculture as a way of life. Whether you're just 
                starting out or nurturing a full farm, we're here to help you grow with ease and confidence.
              </p>
              <p className="text-stone-600 leading-relaxed mb-8">
                Driven by a love for nature, Dreamfield offers quality seeds, rich soil solutions, 
                essential tools, and expert guidance – all in one place. Our state-of-the-art 
                research facilities ensure every seed meets the highest standards.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-primary" />
                  <span className="font-medium text-stone-900">Mission Driven</span>
                </div>
                <div className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-primary" />
                  <span className="font-medium text-stone-900">Vision Focused</span>
                </div>
              </div>

              <Link to="/about">
                <Button className="btn-primary">
                  Learn More About Us
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="relative z-10 rounded-2xl overflow-hidden shadow-strong">
                <img
                  src="/images/about-product.png"
                  alt="Dreamfield product pack"
                  className="w-full h-[500px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-primary/10 rounded-2xl -z-10" />
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-secondary/30 rounded-2xl -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block text-primary font-semibold text-sm mb-4 uppercase tracking-wide">Categories</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-stone-900 mb-4">
              Explore Our Seed Categories
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              From cereals to vegetables, we offer a comprehensive range of high-quality seeds for every farming need.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category, index) => {
              const Icon = category.icon;
              return (
                <motion.div
                  key={category.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link
                    to={`/products?crop_type=${category.name}`}
                    className="block card-hover p-8 text-center group"
                    data-testid={`category-${category.name.toLowerCase()}`}
                  >
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-primary transition-colors duration-300">
                      <Icon className="w-8 h-8 text-primary group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="font-heading font-semibold text-xl text-stone-900 mb-2">{category.name}</h3>
                    <p className="text-stone-600 text-sm">{category.description}</p>
                  </Link>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link to="/products">
              <Button className="btn-primary">
                View All Products
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-primary relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-white rounded-full translate-x-1/2 translate-y-1/2" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block text-secondary font-semibold text-sm mb-4 uppercase tracking-wide">Why Choose Us</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-4">
              The Dreamfield Advantage
            </h2>
            <p className="text-white/80 max-w-2xl mx-auto">
              We're committed to providing farmers with the best seeds and support for successful harvests.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-heading font-semibold text-xl text-white mb-3">{feature.title}</h3>
                  <p className="text-white/70">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl p-12 md:p-16 shadow-medium text-center"
          >
            <div className="w-20 h-20 bg-secondary/20 rounded-full flex items-center justify-center mx-auto mb-8">
              <Droplets className="w-10 h-10 text-secondary" />
            </div>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-stone-900 mb-4">
              Ready to Grow with Us?
            </h2>
            <p className="text-stone-600 text-lg max-w-2xl mx-auto mb-8">
              Join thousands of farmers who trust Dream Field Agri solution for their seed needs. 
              Contact us today to become a dealer or explore our premium seed collection.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/dealer-inquiry">
                <Button className="btn-primary text-base px-8 py-4">
                  Become a Dealer
                </Button>
              </Link>
              <Link to="/contact">
                <Button className="btn-outline text-base px-8 py-4">
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
